/**
 * Shared URL state for sortable / paginated tables.
 *
 * Sort state and page number belong in the URL on every template: it makes a
 * sorted view shareable, keeps the back button honest, and lets the server
 * render exactly what the user (or a crawler) asked for. Templates differ in
 * their columns, not in how this state is encoded — so encode it once here.
 */

export type SortDir = "asc" | "desc";

export interface TableSearch {
  sort?: string | undefined;
  dir?: SortDir | undefined;
  page?: number | undefined;
}

/**
 * Parse table state out of raw search params. Values equal to the defaults are
 * dropped so the canonical URL stays clean (no ?page=1&dir=asc noise).
 */
export function validateTableSearch(
  search: Record<string, unknown>,
  opts: { defaultSort?: string; defaultDir?: SortDir; maxPage?: number } = {},
): TableSearch {
  const defaultSort = opts.defaultSort ?? "rank";
  const defaultDir = opts.defaultDir ?? "asc";
  const maxPage = opts.maxPage ?? 500;

  const sortRaw = typeof search["sort"] === "string" ? search["sort"].slice(0, 40) : "";
  const dirRaw = typeof search["dir"] === "string" ? search["dir"] : "";
  const pageRaw = Number(search["page"]);

  return {
    sort: sortRaw && sortRaw !== defaultSort ? sortRaw : undefined,
    dir: dirRaw === "asc" || dirRaw === "desc" ? (dirRaw !== defaultDir ? dirRaw : undefined) : undefined,
    page: Number.isFinite(pageRaw) && pageRaw > 1 ? Math.min(Math.floor(pageRaw), maxPage) : undefined,
  };
}

/** True when the URL carries non-default table state (so it should not be indexed). */
export function isTableStateFiltered(s: TableSearch): boolean {
  return Boolean(s.sort || s.dir || (s.page ?? 1) > 1);
}

/** Page numbers around the current page; null marks an elided range. */
export function pageWindow(current: number, total: number): (number | null)[] {
  const out: (number | null)[] = [];
  const push = (n: number) => {
    if (n >= 1 && n <= total && !out.includes(n)) out.push(n);
  };
  push(1);
  if (current - 2 > 2) out.push(null);
  for (let n = current - 1; n <= current + 1; n++) push(n);
  if (current + 2 < total - 1) out.push(null);
  push(total);
  return out;
}

/** Slice a list for a 1-based page number. */
export function paginate<T>(rows: T[], page: number, pageSize: number) {
  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
  const current = Math.min(Math.max(1, page), totalPages);
  const start = (current - 1) * pageSize;
  return {
    rows: rows.slice(start, start + pageSize),
    page: current,
    totalPages,
    total: rows.length,
    start,
  };
}
